import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import axios from "axios";
import AppShell from "../components/layout/AppShell";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import Icon from "../components/ui/Icon";
import Modal from "../components/ui/Modal";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import { Parse } from "../lib/parse";
import { MAX_DOCUMENT_FILE_SIZE_MB } from "../config/uploadLimits";
import { useAlert } from "../contexts/AlertContext";

const DOCUMENT_CLASS = "contracts_Document";
const EXTENDED_USER_CLASS = "contracts_Users";
const CONTACTS_REPORT_ID = "contacts";
const DEFAULT_MIN_SIGNATURE_AREA = 1500;
const DEFAULT_MIN_SIGNATURE_LENGTH = 180;
const SUPPORTED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg"];
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RECIPIENT_COLORS = [
  "bg-indigo-100 text-indigo-700 border-indigo-200",
  "bg-emerald-100 text-emerald-700 border-emerald-200",
  "bg-sky-100 text-sky-700 border-sky-200",
  "bg-amber-100 text-amber-700 border-amber-200",
  "bg-rose-100 text-rose-700 border-rose-200",
  "bg-violet-100 text-violet-700 border-violet-200"
];

const parseStoredJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const normalizeCloudResult = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.result)) {
    return payload.result;
  }
  return [];
};

const toPlainObject = (value) => {
  if (!value) return null;
  if (typeof value.toJSON === "function") {
    return value.toJSON();
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (error) {
    return value;
  }
};

const normalizeContactbookRows = (rows = []) => {
  const seenEmails = new Set();
  return (Array.isArray(rows) ? rows : [])
    .map((rawRow) => {
      const row = toPlainObject(rawRow) || {};
      return {
        objectId: String(row?.objectId || row?.id || "").trim(),
        Name: String(row?.Name || row?.name || "").trim(),
        Email: String(row?.Email || row?.email || "")
          .trim()
          .toLowerCase(),
        Phone: String(row?.Phone || row?.phone || "").trim()
      };
    })
    .filter((row) => row.Email)
    .filter((row) => {
      if (seenEmails.has(row.Email)) {
        return false;
      }
      seenEmails.add(row.Email);
      return true;
    });
};

const getParseFunctionBaseUrl = () => {
  const baseUrl = String(localStorage.getItem("baseUrl") || "").trim();
  if (!baseUrl) {
    return "";
  }
  if (baseUrl.startsWith(":")) {
    return `${window.location.protocol}//${window.location.hostname}${baseUrl}`;
  }
  return baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
};

const getParseFunctionHeaders = () => {
  const parseAppId = String(localStorage.getItem("parseAppId") || "").trim();
  const sessionToken = String(localStorage.getItem("accesstoken") || "").trim();
  return {
    "Content-Type": "application/json",
    ...(parseAppId ? { "X-Parse-Application-Id": parseAppId } : {}),
    ...(sessionToken ? { sessiontoken: sessionToken } : {}),
    ...(sessionToken ? { "X-Parse-Session-Token": sessionToken } : {})
  };
};

const readFileAsArrayBuffer = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(event?.target?.result || null);
    reader.onerror = () => reject(reader.error || new Error("file-read-error"));
    reader.readAsArrayBuffer(file);
  });

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(String(event?.target?.result || ""));
    reader.onerror = () => reject(reader.error || new Error("file-read-error"));
    reader.readAsDataURL(file);
  });

const buildPdfFileName = (fileName = "") => {
  const cleanedName = String(fileName || "")
    .trim()
    .replace(/\.[^/.]+$/, "")
    .trim();
  const safeName = (cleanedName || "document")
    .normalize("NFKD")
    .replace(/[^\w.-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  const normalizedSafeName = safeName || `document_${Date.now()}`;
  return `${normalizedSafeName}.pdf`;
};

const convertImageToPdfBytes = async (file) => {
  const { PDFDocument } = await import("pdf-lib");
  const dataUrl = await readFileAsDataUrl(file);
  const pdfDoc = await PDFDocument.create();
  const imageType = String(file?.type || "").toLowerCase();
  const image =
    imageType === "image/png" ? await pdfDoc.embedPng(dataUrl) : await pdfDoc.embedJpg(dataUrl);
  const page = pdfDoc.addPage([image.width, image.height]);
  page.drawImage(image, {
    x: 0,
    y: 0,
    width: image.width,
    height: image.height
  });
  return pdfDoc.save({ useObjectStreams: false });
};

const generateTitleFromFilename = (filename) => {
  try {
    let title = String(filename || "").trim();
    const lastDotIndex = title.lastIndexOf(".");
    if (lastDotIndex > 0) {
      title = title.slice(0, lastDotIndex);
    }
    title = title.replace(/[^\p{L}\p{N}\s-]/gu, " ");
    title = title.replace(/\s+/g, " ");
    title = title.replace(/\p{L}\S*/gu, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase());
    if (title.length > 100) {
      title = title.slice(0, 100).trim();
    }
    return title || "Untitled Document";
  } catch (error) {
    return "Untitled Document";
  }
};

const parseRecipientQueryInput = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) {
    return null;
  }

  // Strategy 1: Accept "name email@domain.com" or "name + email@domain.com" styles.
  const inlineEmailMatch = rawValue.match(/[^\s<>@]+@[^\s<>@]+\.[^\s<>@]+/);
  if (inlineEmailMatch) {
    const parsedEmail = String(inlineEmailMatch[0] || "").trim().toLowerCase();
    if (EMAIL_REGEX.test(parsedEmail)) {
      const parsedName = rawValue
        .replace(inlineEmailMatch[0], "")
        .replace(/[<>()[\],;+]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
      return {
        name: parsedName || parsedEmail,
        email: parsedEmail
      };
    }
  }

  // Strategy 2: Named email format "Name <email@domain.com>"
  const namedEmailMatch = rawValue.match(/^(.*?)<\s*([^<>]+)\s*>$/);
  if (namedEmailMatch) {
    const parsedEmail = String(namedEmailMatch[2] || "").trim().toLowerCase();
    const parsedName = String(namedEmailMatch[1] || "").trim();
    if (EMAIL_REGEX.test(parsedEmail)) {
      return {
        name: parsedName || parsedEmail,
        email: parsedEmail
      };
    }
  }

  // Strategy 3: Email only "email@domain.com"
  const emailOnlyMatch = rawValue.match(/^([^\s<>@]+@[^\s<>@]+\.[^\s<>@]+)$/);
  if (emailOnlyMatch) {
    const parsedEmail = String(emailOnlyMatch[1] || "").trim().toLowerCase();
    if (EMAIL_REGEX.test(parsedEmail)) {
      return {
        name: parsedEmail,
        email: parsedEmail
      };
    }
  }

  return null;
};

const isValidHttpUrl = (value) => {
  try {
    const parsedUrl = new URL(String(value || "").trim());
    return parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
  } catch (error) {
    return false;
  }
};

function InfoHint({ text = "" }) {
  const [isOpen, setIsOpen] = useState(false);

  if (!text) {
    return null;
  }

  return (
    <span
      className="relative ml-1 inline-flex"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button
        type="button"
        className="inline-flex h-4 w-4 items-center justify-center rounded-full text-slate-400 hover:text-slate-600 transition-colors"
        aria-label={text}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 20 20" 
          fill="currentColor" 
          className="w-4 h-4"
        >
          <path 
            fillRule="evenodd" 
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM8.94 6.94a.75.75 0 11-1.061-1.061 3 3 0 112.871 5.026v.345a.75.75 0 01-1.5 0v-.5c0-.72.57-1.172 1.081-1.287A1.5 1.5 0 108.94 6.94zM10 15a1 1 0 100-2 1 1 0 000 2z" 
            clipRule="evenodd" 
          />
        </svg>
      </button>
      {isOpen ? (
        <span className="pointer-events-none absolute left-1/2 top-[calc(100%+8px)] z-[9999] w-[320px] -translate-x-1/2 rounded-lg border border-slate-200 bg-white p-3 text-left text-xs leading-relaxed text-slate-700 shadow-lg">
          {text}
        </span>
      ) : null}
    </span>
  );
}

function RequestSignPage() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { showError } = useAlert();
  const fileInputRef = useRef(null);
  const recipientPickerRef = useRef(null);
  const recipientPickerAnchorRef = useRef(null);
  const folderPickerRef = useRef(null);

  const extUser = useMemo(() => {
    const extClass = parseStoredJson("Extand_Class");
    return Array.isArray(extClass) ? extClass?.[0] || {} : {};
  }, []);

  const defaultSendInOrder = extUser?.SendinOrder !== undefined ? Boolean(extUser.SendinOrder) : true;
  const defaultIsTourEnabled = extUser?.IsTourEnabled !== undefined ? Boolean(extUser.IsTourEnabled) : true;
  const defaultIsEnableOTP = extUser?.IsEnableOTP !== undefined ? Boolean(extUser.IsEnableOTP) : false;
  const defaultNotifyOnSignatures =
    extUser?.NotifyOnSignatures !== undefined ? Boolean(extUser.NotifyOnSignatures) : true;
  const defaultTimeToCompleteDays = (() => {
    const parsed = Number.parseInt(extUser?.DefaultTimeToCompleteDays, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 15;
  })();

  const [folders, setFolders] = useState([]);
  const [isLoadingFolders, setIsLoadingFolders] = useState(true);
  const [fileName, setFileName] = useState("");
  const [uploadedFileUrl, setUploadedFileUrl] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFolderPickerOpen, setIsFolderPickerOpen] = useState(false);
  const [recipientQuery, setRecipientQuery] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [requireBiometricForAll, setRequireBiometricForAll] = useState(false);
  const [showDocumentBackModal, setShowDocumentBackModal] = useState(false);
  const [requireDocumentBackForAll, setRequireDocumentBackForAll] = useState(true);
  const [isRecipientPickerOpen, setIsRecipientPickerOpen] = useState(false);
  const [recipientPickerDirection, setRecipientPickerDirection] = useState("down");
  const [recipientPickerMaxHeight, setRecipientPickerMaxHeight] = useState(250);
  const [contactbookRows, setContactbookRows] = useState([]);
  const [isLoadingContactbook, setIsLoadingContactbook] = useState(true);
  const [isAddContactModalOpen, setIsAddContactModalOpen] = useState(false);
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [contactFormError, setContactFormError] = useState("");
  const [contactFormFieldErrors, setContactFormFieldErrors] = useState({ Name: "", Email: "" });
  const [contactForm, setContactForm] = useState({ Name: "", Email: "", Phone: "" });
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState({ show: false, recipientId: null, recipientName: "" });
  const [advancedOptions, setAdvancedOptions] = useState(() => ({
    sendInOrder: defaultSendInOrder,
    automaticReminders: false,
    remindOnceInEvery: 5,
    timeToCompleteDays: defaultTimeToCompleteDays,
    isTourEnabled: defaultIsTourEnabled,
    notifyOnSignatures: defaultNotifyOnSignatures,
    isEnableOTP: defaultIsEnableOTP,
    redirectUrl: ""
  }));
  const getLocalizedDefaultNote = () =>
    t("new-ui.request-sign.default-note", {
      defaultValue: "Please review and sign this document."
    });
  const [formState, setFormState] = useState(() => ({
    title: "",
    note: getLocalizedDefaultNote(),
    folderId: ""
  }));
  const selectedFolderLabel = useMemo(() => {
    if (!formState.folderId) {
      return t("new-ui.sign-yourself.no-folder", { defaultValue: "No folder" });
    }
    const selectedFolder = folders.find((folder) => folder.objectId === formState.folderId);
    return selectedFolder?.Name || selectedFolder?.objectId || formState.folderId;
  }, [folders, formState.folderId, t]);

  useEffect(() => {
    const nextDefaultNote = getLocalizedDefaultNote();
    setFormState((current) => {
      const currentNote = String(current?.note || "").trim();
      const knownDefaultNotes = new Set([
        "Please review and sign this document.",
        "Por favor revisa y firma este documento."
      ]);
      if (!currentNote || knownDefaultNotes.has(currentNote)) {
        return { ...current, note: nextDefaultNote };
      }
      return current;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.resolvedLanguage, i18n.language]);

  const loadFolders = async () => {
    setIsLoadingFolders(true);
    try {
      const currentUser = Parse.User.current();
      if (!currentUser) {
        throw new Error(t("invalid-session", { defaultValue: "Invalid session." }));
      }
      const folderQuery = new Parse.Query(DOCUMENT_CLASS);
      folderQuery.equalTo("Type", "Folder");
      folderQuery.equalTo("CreatedBy", currentUser);
      folderQuery.notEqualTo("IsArchive", true);
      folderQuery.ascending("Name");
      folderQuery.limit(200);
      const folderResults = await folderQuery.find();
      setFolders(
        folderResults.map((folder) => ({
          objectId: folder.id,
          Name: folder.get("Name") || ""
        }))
      );
    } catch (loadError) {
      console.error("Request signatures folder load failed", loadError);
      showError(
        loadError?.message ||
          t("new-ui.dashboard.errors.load-failed", {
            defaultValue: "Could not load the dashboard from backend."
          })
      );
    } finally {
      setIsLoadingFolders(false);
    }
  };

  useEffect(() => {
    loadFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadContactbook = async () => {
    setIsLoadingContactbook(true);
    try {
      let normalizedContacts = [];

      try {
        const cloudResponse = await Parse.Cloud.run("getReport", {
          reportId: CONTACTS_REPORT_ID,
          skip: 0,
          limit: 300
        });
        normalizedContacts = normalizeContactbookRows(normalizeCloudResult(cloudResponse));
      } catch (reportError) {
        normalizedContacts = [];
      }

      if (!normalizedContacts.length) {
        const queryLimit = 300;
        const currentUser = Parse.User.current();
        const tenantId = String(extUser?.TenantId?.objectId || localStorage.getItem("TenantId") || "").trim();
        const tenantPointer = tenantId ? Parse.Object.extend("partners_Tenant").createWithoutData(tenantId) : null;

        if (currentUser) {
          try {
            const byUserQuery = new Parse.Query("contracts_Contactbook");
            byUserQuery.equalTo("CreatedBy", currentUser);
            byUserQuery.notEqualTo("IsDeleted", true);
            byUserQuery.descending("updatedAt");
            byUserQuery.limit(queryLimit);
            const byUserRows = await byUserQuery.find();
            normalizedContacts = normalizeContactbookRows(byUserRows);
          } catch (userQueryError) {
            normalizedContacts = [];
          }
        }

        if (!normalizedContacts.length && tenantPointer) {
          try {
            const byTenantQuery = new Parse.Query("contracts_Contactbook");
            byTenantQuery.equalTo("TenantId", tenantPointer);
            byTenantQuery.notEqualTo("IsDeleted", true);
            byTenantQuery.descending("updatedAt");
            byTenantQuery.limit(queryLimit);
            const byTenantRows = await byTenantQuery.find();
            normalizedContacts = normalizeContactbookRows(byTenantRows);
          } catch (tenantQueryError) {
            normalizedContacts = [];
          }
        }

        if (!normalizedContacts.length) {
          const baseUrl = getParseFunctionBaseUrl();
          if (baseUrl) {
            try {
              const signerResponse = await axios.post(
                `${baseUrl}functions/getsigners`,
                { search: "" },
                { headers: getParseFunctionHeaders() }
              );
              normalizedContacts = normalizeContactbookRows(signerResponse?.data?.result || []);
            } catch (signersError) {
              normalizedContacts = [];
            }
          }
        }
      }

      setContactbookRows(normalizedContacts);
    } catch (contactError) {
      console.error("Request signatures contactbook load failed", contactError);
      setContactbookRows([]);
    } finally {
      setIsLoadingContactbook(false);
    }
  };

  useEffect(() => {
    loadContactbook();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (recipientPickerRef.current && !recipientPickerRef.current.contains(event.target)) {
        setIsRecipientPickerOpen(false);
      }
      if (folderPickerRef.current && !folderPickerRef.current.contains(event.target)) {
        setIsFolderPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const updateRecipientPickerLayout = useCallback(() => {
    if (!recipientPickerAnchorRef.current) {
      return;
    }
    const anchorRect = recipientPickerAnchorRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const margin = 12;
    const preferredHeight = 250;
    const minHeight = 140;
    const spaceBelow = Math.max(0, viewportHeight - anchorRect.bottom - margin);
    const spaceAbove = Math.max(0, anchorRect.top - margin);
    const openUp = spaceBelow < 220 && spaceAbove > spaceBelow;
    const availableSpace = openUp ? spaceAbove : spaceBelow;
    const nextMaxHeight = Math.max(minHeight, Math.min(preferredHeight, availableSpace - 8));

    setRecipientPickerDirection(openUp ? "up" : "down");
    setRecipientPickerMaxHeight(nextMaxHeight);
  }, []);

  useEffect(() => {
    if (!isRecipientPickerOpen) {
      return;
    }
    updateRecipientPickerLayout();
    const handleViewportChange = () => updateRecipientPickerLayout();
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);
    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [isRecipientPickerOpen, updateRecipientPickerLayout]);

  // Handle requireBiometricForAll checkbox change
  const handleRequireBiometricForAllChange = (checked) => {
    if (checked) {
      // Show modal to ask about document back
      setShowDocumentBackModal(true);
    } else {
      // If unchecking, just disable biometric for all
      setRequireBiometricForAll(false);
    }
  };

  // Handle document back modal confirmation
  const handleDocumentBackModalConfirm = (requireBack) => {
    setRequireDocumentBackForAll(requireBack);
    setRequireBiometricForAll(true);
    setShowDocumentBackModal(false);
  };

  // Handle document back modal cancel
  const handleDocumentBackModalCancel = () => {
    setShowDocumentBackModal(false);
    // Don't enable biometric if user cancels
  };

  // Update all recipients when requireBiometricForAll changes
  useEffect(() => {
    if (recipients.length > 0) {
      setRecipients((current) =>
        current.map((recipient) => ({
          ...recipient,
          requireBiometric: requireBiometricForAll,
          verificationSettings: requireBiometricForAll
            ? {
                enabled: true,
                threshold: 85,
                requireDocumentBack: requireDocumentBackForAll,
                notes: ""
              }
            : {}
        }))
      );
    }
  }, [requireBiometricForAll, requireDocumentBackForAll]);

  const triggerFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setUploadProgress(0);
    setUploadedFileUrl("");
    setFileName("");

    const fileSizeMb = file.size / (1024 * 1024);
    if (fileSizeMb > MAX_DOCUMENT_FILE_SIZE_MB) {
      showError(
        `${t("file-alert-1", { defaultValue: "Selected file is too large." })} ${MAX_DOCUMENT_FILE_SIZE_MB} MB.`
      );
      event.target.value = "";
      return;
    }
    const normalizedType = String(file.type || "").toLowerCase();
    const isPdf = normalizedType === "application/pdf" || String(file.name || "").toLowerCase().endsWith(".pdf");
    const isSupportedImage = SUPPORTED_IMAGE_TYPES.includes(normalizedType);
    if (!isPdf && !isSupportedImage) {
      showError(
        t("new-ui.sign-yourself.only-supported-files", {
          defaultValue: "Only PDF, PNG, JPG and JPEG files are supported in this flow."
        })
      );
      event.target.value = "";
      return;
    }

    try {
      setIsUploading(true);
      const uploadName = buildPdfFileName(file.name);
      const uploadBytes = isPdf
        ? new Uint8Array(await readFileAsArrayBuffer(file))
        : new Uint8Array(await convertImageToPdfBytes(file));
      const parseFile = new Parse.File(uploadName, Array.from(uploadBytes), "application/pdf");
      const fileResult = await parseFile.save({
        progress: (progressValue, loaded, total, meta = {}) => {
          if (meta.type === "upload" && total > 0) {
            setUploadProgress(Math.max(0, Math.min(100, Math.round((loaded * 100) / total))));
            return;
          }
          if (typeof progressValue === "number") {
            setUploadProgress(Math.max(0, Math.min(100, Math.round(progressValue * 100))));
          }
        }
      });

      const sourceUrl = fileResult?.url?.() || parseFile.url();
      if (!sourceUrl) {
        throw new Error(
          t("new-ui.request-sign.upload-failed", {
            defaultValue: "Could not upload file."
          })
        );
      }

      setUploadedFileUrl(sourceUrl);
      setFileName(isSupportedImage ? `${file.name} (PDF)` : file.name);
      setFormState((current) => ({
        ...current,
        title: current.title || generateTitleFromFilename(file.name)
      }));
    } catch (uploadError) {
      console.error("Request signatures upload failed", uploadError);
      showError(
        uploadError?.message ||
          t("new-ui.request-sign.upload-failed", {
            defaultValue: "Could not upload file."
          })
      );
      event.target.value = "";
    } finally {
      setIsUploading(false);
    }
  };

  const clearForm = () => {
    setFileName("");
    setUploadedFileUrl("");
    setUploadProgress(0);
    setRecipients([]);
    setRecipientQuery("");
    setIsRecipientPickerOpen(false);
    setIsAdvancedOpen(false);
    setFormState({
      title: "",
      note: getLocalizedDefaultNote(),
      folderId: ""
    });
    setAdvancedOptions({
      sendInOrder: defaultSendInOrder,
      automaticReminders: false,
      remindOnceInEvery: 5,
      timeToCompleteDays: defaultTimeToCompleteDays,
      isTourEnabled: defaultIsTourEnabled,
      notifyOnSignatures: defaultNotifyOnSignatures,
      isEnableOTP: defaultIsEnableOTP,
      redirectUrl: ""
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const addRecipientByData = ({ name, email, objectId }) => {
    const normalizedEmail = String(email || "").trim().toLowerCase();
    const normalizedName = String(name || "").trim();
    if (!normalizedEmail) {
      showError(
        t("new-ui.request-sign.recipient-email-required", {
          defaultValue: "Signer email is required."
        })
      );
      return false;
    }
    if (!EMAIL_REGEX.test(normalizedEmail)) {
      showError(
        t("new-ui.request-sign.recipient-email-invalid", {
          defaultValue: "Signer email is invalid."
        })
      );
      return false;
    }
    if (recipients.some((item) => item.email === normalizedEmail)) {
      showError(
        t("new-ui.request-sign.recipient-email-duplicate", {
          defaultValue: "This recipient is already added."
        })
      );
      return false;
    }
    console.log("Adding recipient with requireBiometricForAll:", requireBiometricForAll);
    const nextRecipient = {
      id: objectId || `recipient-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      objectId: objectId || "",
      name: normalizedName || normalizedEmail,
      email: normalizedEmail,
      colorClass: RECIPIENT_COLORS[recipients.length % RECIPIENT_COLORS.length],
      requireBiometric: requireBiometricForAll,
      verificationSettings: requireBiometricForAll
        ? {
            enabled: true,
            threshold: 85,
            notes: ""
          }
        : {}
    };
    console.log("Created recipient:", nextRecipient);
    setRecipients((current) => [...current, nextRecipient]);
    return true;
  };

  const filteredContactbook = useMemo(() => {
    const query = String(recipientQuery || "").trim().toLowerCase();
    const availableContacts = contactbookRows.filter(
      (contact) => !recipients.some((recipient) => recipient.email === contact.Email)
    );
    if (!query) {
      return availableContacts.slice(0, 30);
    }
    return availableContacts
      .filter((contact) => `${contact.Name} ${contact.Email} ${contact.Phone}`.toLowerCase().includes(query))
      .slice(0, 30);
  }, [recipientQuery, contactbookRows, recipients]);

  const addRecipientFromContactbook = (contact) => {
    if (!contact?.Email) {
      showError(
        t("new-ui.request-sign.contact-email-invalid", {
          defaultValue: "The selected contact does not have a valid email."
        })
      );
      return;
    }
    const added = addRecipientByData({
      name: contact.Name || contact.Email,
      email: contact.Email,
      objectId: contact.objectId
    });
    if (added) {
      setRecipientQuery("");
      setIsRecipientPickerOpen(false);
    }
  };

  const addRecipientFromQuery = () => {
    const parsed = parseRecipientQueryInput(recipientQuery);
    if (!parsed) {
      showError(
        t("new-ui.request-sign.recipient-search-invalid", {
          defaultValue: "Escribe un email válido o el formato: Nombre email@ejemplo.com"
        })
      );
      return;
    }
    const added = addRecipientByData(parsed);
    if (added) {
      setRecipientQuery("");
      setIsRecipientPickerOpen(false);
    }
  };

  const openAddContactModal = () => {
    setContactForm({ Name: "", Email: "", Phone: "" });
    setContactFormError("");
    setContactFormFieldErrors({ Name: "", Email: "" });
    setIsAddContactModalOpen(true);
  };

  const closeAddContactModal = () => {
    if (isSavingContact) {
      return;
    }
    setIsAddContactModalOpen(false);
    setContactFormError("");
    setContactFormFieldErrors({ Name: "", Email: "" });
  };

  const saveContactAndAddRecipient = async () => {
    const name = String(contactForm?.Name || "").trim();
    const email = String(contactForm?.Email || "")
      .trim()
      .toLowerCase();
    const phone = String(contactForm?.Phone || "").trim();

    const fieldErrors = {
      Name: "",
      Email: ""
    };
    if (!name) {
      fieldErrors.Name = t("input-required", { defaultValue: "Please fill out this field." });
    }
    if (!email) {
      fieldErrors.Email = t("input-required", { defaultValue: "Please fill out this field." });
    }
    if (fieldErrors.Name || fieldErrors.Email) {
      setContactFormFieldErrors(fieldErrors);
      setContactFormError("");
      return;
    }
    if (!EMAIL_REGEX.test(email)) {
      setContactFormFieldErrors({
        Name: "",
        Email: t("new-ui.request-sign.recipient-email-invalid", {
          defaultValue: "Signer email is invalid."
        })
      });
      setContactFormError("");
      return;
    }

    setIsSavingContact(true);
    setContactFormError("");
    setContactFormFieldErrors({ Name: "", Email: "" });
    try {
      let tenantId = String(extUser?.TenantId?.objectId || localStorage.getItem("TenantId") || "").trim();
      if (!tenantId) {
        const currentUserId = Parse.User.current()?.id || "";
        if (currentUserId) {
          try {
            const tenantPayload = await Parse.Cloud.run("gettenant", { userId: currentUserId });
            tenantId = String(tenantPayload?.objectId || tenantPayload?.id || "").trim();
          } catch (tenantError) {
            tenantId = "";
          }
        }
      }

      const createPayload = {
        name,
        email,
        phone
      };
      if (tenantId) {
        createPayload.tenantId = tenantId;
      }

      const savedContact = await Parse.Cloud.run("savecontact", createPayload);
      const nextContact = {
        objectId: String(savedContact?.objectId || savedContact?.id || `contact-${Date.now()}`).trim(),
        Name: name,
        Email: email,
        Phone: phone
      };
      setContactbookRows((current) => {
        if (current.some((item) => item.Email === nextContact.Email)) {
          return current;
        }
        return [nextContact, ...current];
      });
      addRecipientByData({
        name: nextContact.Name,
        email: nextContact.Email,
        objectId: nextContact.objectId
      });
      setRecipientQuery("");
      setIsRecipientPickerOpen(false);
      setIsAddContactModalOpen(false);
    } catch (saveError) {
      const message = String(saveError?.message || "").toLowerCase();
      if (message.includes("already exists")) {
        setContactFormError(t("add-signer-alert", { defaultValue: "Contact already exists!" }));
      } else {
        setContactFormError(
          saveError?.message ||
            t("something-went-wrong-mssg", {
              defaultValue: "Something went wrong."
            })
        );
      }
    } finally {
      setIsSavingContact(false);
    }
  };

  const removeRecipient = (recipientId) => {
    // Find the recipient to show their name in the confirmation
    const recipient = recipients.find((r) => r.id === recipientId);
    const recipientName = recipient?.name || recipient?.email || t("this-signer", { defaultValue: "este firmante" });
    
    // Show confirmation dialog
    setConfirmDelete({
      show: true,
      recipientId: recipientId,
      recipientName: recipientName
    });
  };

  const handleConfirmDelete = () => {
    if (confirmDelete.recipientId) {
      setRecipients((current) => current.filter((item) => item.id !== confirmDelete.recipientId));
    }
    setConfirmDelete({ show: false, recipientId: null, recipientName: "" });
  };

  const handleCancelDelete = () => {
    setConfirmDelete({ show: false, recipientId: null, recipientName: "" });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!uploadedFileUrl) {
      showError(t("file-alert-3", { defaultValue: "Please wait while the document uploads." }));
      return;
    }
    const trimmedTitle = String(formState.title || "").trim();
    if (!trimmedTitle) {
      showError(
        t("new-ui.sign-yourself.title-required", {
          defaultValue: "Document title is required."
        })
      );
      return;
    }
    if (!recipients.length) {
      showError(
        t("new-ui.request-sign.recipients-required", {
          defaultValue: "Add at least one recipient before opening the editor."
        })
      );
      return;
    }

    const timeToCompleteDays = Number.parseInt(advancedOptions.timeToCompleteDays, 10);
    const remindOnceInEvery = Number.parseInt(advancedOptions.remindOnceInEvery, 10);
    const redirectUrl = String(advancedOptions.redirectUrl || "").trim();

    if (!Number.isFinite(timeToCompleteDays) || timeToCompleteDays <= 0) {
      showError(t("input-required", { defaultValue: "Please fill out this field." }));
      return;
    }
    if (advancedOptions.automaticReminders) {
      if (!Number.isFinite(remindOnceInEvery) || remindOnceInEvery <= 0) {
        showError(t("input-required", { defaultValue: "Please fill out this field." }));
        return;
      }
      const reminderCount = timeToCompleteDays / remindOnceInEvery;
      if (reminderCount > 15) {
        showError(
          t("only-15-reminder-allowed", {
            defaultValue:
              "You can set up to 15 automatic reminders. For example, if 'TimeToComplete' is 15 days and 'RemindOnceInEvery' is 1 day, you'll reach the maximum limit of 15 reminders. Adjust your settings accordingly."
          })
        );
        return;
      }
    }
    if (redirectUrl && !isValidHttpUrl(redirectUrl)) {
      showError(
        t("invalid-redirect-url", {
          defaultValue: "Please provide valid redirect url."
        })
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const currentUser = Parse.User.current();
      if (!currentUser?.id) {
        throw new Error(t("invalid-session", { defaultValue: "Invalid session." }));
      }

      const documentObject = new Parse.Object(DOCUMENT_CLASS);
      documentObject.set("Name", trimmedTitle);
      documentObject.set("Note", String(formState.note || "").trim());
      documentObject.set("URL", uploadedFileUrl);
      documentObject.set("CreatedBy", Parse.User.createWithoutData(currentUser.id));
      documentObject.set("SentToOthers", true);
      documentObject.set("IsSignyourself", false);
      documentObject.set("IsDraft", true);
      documentObject.set("NewUIRecipients", recipients);
      documentObject.set("SendinOrder", advancedOptions.sendInOrder);
      documentObject.set("AutomaticReminders", advancedOptions.automaticReminders);
      documentObject.set("RemindOnceInEvery", Number.isFinite(remindOnceInEvery) ? remindOnceInEvery : 5);
      documentObject.set("TimeToCompleteDays", timeToCompleteDays);
      documentObject.set("AllowModifications", false);
      documentObject.set("IsEnableOTP", advancedOptions.isEnableOTP);
      documentObject.set("IsTourEnabled", advancedOptions.isTourEnabled);
      documentObject.set("NotifyOnSignatures", advancedOptions.notifyOnSignatures);
      if (redirectUrl) {
        documentObject.set("RedirectUrl", redirectUrl);
      }
      const tenantMinSignatureArea = Number.parseInt(extUser?.TenantId?.MinSignatureArea, 10);
      const tenantMinSignatureLength = Number.parseInt(extUser?.TenantId?.MinSignatureLength, 10);
      documentObject.set(
        "MinSignatureArea",
        tenantMinSignatureArea || Number.parseInt(extUser?.MinSignatureArea, 10) || DEFAULT_MIN_SIGNATURE_AREA
      );
      documentObject.set(
        "MinSignatureLength",
        tenantMinSignatureLength ||
          Number.parseInt(extUser?.MinSignatureLength, 10) ||
          DEFAULT_MIN_SIGNATURE_LENGTH
      );

      if (formState.folderId) {
        documentObject.set("Folder", {
          __type: "Pointer",
          className: DOCUMENT_CLASS,
          objectId: formState.folderId
        });
      }
      if (extUser?.objectId) {
        documentObject.set("ExtUserPtr", {
          __type: "Pointer",
          className: EXTENDED_USER_CLASS,
          objectId: extUser.objectId
        });
      }
      if (extUser?.TenantId?.ActiveFileAdapter) {
        documentObject.set("FileAdapterId", extUser.TenantId.ActiveFileAdapter);
      }

      const savedDocument = await documentObject.save();
      navigate(`/requestSignPdf/${savedDocument?.id}`, {
        state: {
          recipients,
          backTo: `${location.pathname}${location.search || ""}`
        }
      });
    } catch (saveError) {
      console.error("Failed to create request-sign document", saveError);
      showError(
        saveError?.message ||
          t("something-went-wrong-mssg", {
            defaultValue: "Something went wrong."
          })
      );
      setIsSubmitting(false);
    }
  };

  return (
    <AppShell>
      <div className="dana-section-grid">
        <Card className="animate-fade-up">
          <div className="dana-section-head">
            <span className="dana-section-icon">
              <Icon name="mail" />
            </span>
            <div>
              <p className="ui-kicker">
                {t("new-ui.request-sign.kicker", { defaultValue: "Cargar y enviar" })}
              </p>
              <h1 className="ui-title dana-section-title">
                {t("new-ui.request-sign.page-title", { defaultValue: "Solicitar firmas a terceros" })}
              </h1>
            </div>
          </div>
          <p className="ui-copy mt-3">
            {t("new-ui.request-sign.description", {
              defaultValue: "Upload a document, add recipients, and assign fields in the editor."
            })}
          </p>
        </Card>

        <Card className="animate-fade-up [animation-delay:40ms]">
          <form className="grid grid-cols-1 gap-3" onSubmit={handleSubmit}>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-app-ink-muted">
                {`${t("report-heading.File", { defaultValue: "Archivo" })} (${t("file-type", {
                  defaultValue: "pdf, png, jpg, jpeg"
                })})`}
              </label>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileChange}
                accept="application/pdf,image/png,image/jpeg,image/jpg"
              />
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  onClick={triggerFilePicker}
                  disabled={isUploading || isSubmitting}
                  className="!border !border-[#1ea34f] !bg-[#22c55e] !text-white hover:!bg-[#1ea34f] focus-visible:!ring-[rgba(34,197,94,0.35)]"
                >
                  <span className="inline-flex items-center gap-2">
                    <Icon name="templateDoc" />
                    {t("new-ui.sign-yourself.choose-file", { defaultValue: "Cargar archivo" })}
                  </span>
                </Button>
                {fileName ? (
                  <p className="m-0 truncate text-xs text-app-ink-muted" title={fileName}>
                    {t("file-selected", { defaultValue: "Selected file" })}: {fileName}
                  </p>
                ) : (
                  <p className="m-0 text-xs text-app-ink-muted">
                    {t("new-ui.sign-yourself.no-file", { defaultValue: "No file selected." })}
                  </p>
                )}
              </div>
              <p className="m-0 mt-1 text-[11px] text-app-ink-muted">
                {t("new-ui.upload.max-size", {
                  defaultValue: "Maximum file size: {{size}} MB",
                  size: MAX_DOCUMENT_FILE_SIZE_MB
                })}
              </p>
              {isUploading ? (
                <div className="mt-2">
                  <div className="h-1.5 w-full rounded-full bg-slate-200">
                    <div className="h-1.5 rounded-full bg-app-brand transition-all" style={{ width: `${uploadProgress}%` }} />
                  </div>
                  <p className="m-0 mt-1 text-[11px] text-app-ink-muted">{uploadProgress}%</p>
                </div>
              ) : null}
            </div>

            <Input
              id="request-sign-title"
              label={t("document-title", { defaultValue: "Document title" })}
              value={formState.title}
              onChange={(event) => setFormState((current) => ({ ...current, title: event.target.value }))}
              required
            />

            <div className="w-full">
              <label
                htmlFor="request-sign-note"
                className="mb-1.5 block text-xs font-semibold text-app-ink-muted"
              >
                {t("report-heading.Note", { defaultValue: "Nota" })}
              </label>
              <textarea
                id="request-sign-note"
                className="min-h-[86px] w-full rounded-xl border border-app-line bg-white px-3 py-2.5 text-sm text-app-ink shadow-soft outline-none transition focus:border-app-brand focus:ring-4 focus:ring-app-brand/15"
                value={formState.note}
                onChange={(event) => setFormState((current) => ({ ...current, note: event.target.value }))}
              />
            </div>

            <div className="w-full">
              <label
                htmlFor="request-sign-folder"
                className="mb-1.5 block text-xs font-semibold text-app-ink-muted"
              >
                {t("select-folder", { defaultValue: "Seleccionar carpeta" })}
              </label>
              <div className="relative" ref={folderPickerRef}>
                <button
                  id="request-sign-folder"
                  type="button"
                  className="flex w-full items-center justify-between rounded-xl border border-app-line bg-white px-3 py-2.5 text-left text-sm text-app-ink shadow-soft outline-none transition hover:border-app-brand/60 focus:border-app-brand focus:ring-4 focus:ring-app-brand/15 disabled:cursor-not-allowed disabled:opacity-70"
                  onClick={() => setIsFolderPickerOpen((current) => !current)}
                  disabled={isLoadingFolders}
                  aria-haspopup="listbox"
                  aria-expanded={isFolderPickerOpen ? "true" : "false"}
                >
                  <span className="truncate">{selectedFolderLabel}</span>
                  <Icon name="chevronDown" className="h-4 w-4 text-app-ink-muted" />
                </button>
                {isFolderPickerOpen ? (
                  <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-[1200] max-h-56 overflow-auto rounded-xl border border-app-line bg-white shadow-soft">
                    <button
                      type="button"
                      role="option"
                      aria-selected={!formState.folderId}
                      className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm transition hover:bg-slate-100 ${
                        !formState.folderId ? "bg-slate-100 text-app-ink" : "text-app-ink"
                      }`}
                      onClick={() => {
                        setFormState((current) => ({ ...current, folderId: "" }));
                        setIsFolderPickerOpen(false);
                      }}
                    >
                      <span className="truncate">{t("new-ui.sign-yourself.no-folder", { defaultValue: "No folder" })}</span>
                    </button>
                    {folders.map((folder) => {
                      const isSelected = formState.folderId === folder.objectId;
                      return (
                        <button
                          key={folder.objectId}
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm transition hover:bg-slate-100 ${
                            isSelected ? "bg-slate-100 text-app-ink" : "text-app-ink"
                          }`}
                          onClick={() => {
                            setFormState((current) => ({ ...current, folderId: folder.objectId }));
                            setIsFolderPickerOpen(false);
                          }}
                        >
                          <Icon name="folder" className="h-4 w-4 shrink-0 text-slate-400" />
                          <span className="truncate">{folder.Name || folder.objectId}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="rounded-xl border border-app-line bg-app-surface p-3">
              <p className="m-0 text-xs font-semibold text-app-ink-muted">
                {t("new-ui.request-sign.recipients", { defaultValue: "Firmantes" })}
                <span className="ml-1 text-app-danger">*</span>
              </p>
              <div className="mt-2 flex flex-col gap-2 md:flex-row md:items-start">
                <div className="relative min-w-0 w-full flex-1" ref={recipientPickerRef}>
                  <div className="relative" ref={recipientPickerAnchorRef}>
                    <input
                      id="request-sign-recipient-query"
                      type="text"
                      value={recipientQuery}
                      onChange={(event) => {
                        setRecipientQuery(event.target.value);
                        setIsRecipientPickerOpen(true);
                        requestAnimationFrame(updateRecipientPickerLayout);
                      }}
                      onFocus={() => {
                        setIsRecipientPickerOpen(true);
                        requestAnimationFrame(updateRecipientPickerLayout);
                        if (!isLoadingContactbook && !contactbookRows.length) {
                          loadContactbook();
                        }
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          const exactMatch = filteredContactbook.find(
                            (contact) =>
                              String(contact.Email || "").toLowerCase() ===
                                String(recipientQuery || "").trim().toLowerCase() ||
                              String(contact.Name || "").toLowerCase() ===
                                String(recipientQuery || "").trim().toLowerCase()
                          );
                          if (exactMatch) {
                            addRecipientFromContactbook(exactMatch);
                            return;
                          }
                          addRecipientFromQuery();
                        }
                      }}
                      placeholder={t("new-ui.request-sign.recipient-query-placeholder", {
                        defaultValue: "Type name or email"
                      })}
                      className="w-full rounded-xl border border-app-line bg-white px-3 py-2.5 pr-10 text-sm text-app-ink shadow-soft outline-none transition focus:border-app-brand focus:ring-4 focus:ring-app-brand/15"
                    />
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 inline-flex -translate-y-1/2 items-center justify-center rounded-md p-1 text-app-ink-muted hover:bg-slate-100 hover:text-app-ink"
                      onClick={() => {
                        setIsRecipientPickerOpen((current) => {
                          const nextValue = !current;
                          if (nextValue) {
                            requestAnimationFrame(updateRecipientPickerLayout);
                          }
                          return nextValue;
                        });
                      }}
                      aria-label={t("new-ui.request-sign.select-contact", { defaultValue: "Select contact" })}
                    >
                      <Icon name="chevronDown" className="h-4 w-4" />
                    </button>
                  </div>
                  {isRecipientPickerOpen ? (
                    <div
                      className={`absolute left-0 right-0 z-[1400] overflow-auto rounded-xl border border-app-line bg-white shadow-soft ${
                        recipientPickerDirection === "up" ? "bottom-[calc(100%+6px)]" : "top-[calc(100%+6px)]"
                      }`}
                      style={{ maxHeight: `${recipientPickerMaxHeight}px` }}
                    >
                      {isLoadingContactbook ? (
                        <p className="m-0 px-3 py-2.5 text-sm text-app-ink-muted">
                          {t("loading", { defaultValue: "Loading..." })}
                        </p>
                      ) : filteredContactbook.length ? (
                        <ul className="m-0 list-none p-1">
                          {filteredContactbook.map((contact) => (
                            <li key={contact.objectId}>
                              <button
                                type="button"
                                onClick={() => addRecipientFromContactbook(contact)}
                                className="block w-full rounded-lg px-2.5 py-2 text-left text-sm text-app-ink transition hover:bg-slate-100"
                              >
                                {contact.Name || contact.Email}
                                <span className="ml-1 text-app-ink-muted">{`<${contact.Email}>`}</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="m-0 px-3 py-2.5 text-sm text-app-ink-muted">
                          {t("new-ui.dashboard.recent-sent-documents.no-results", {
                            defaultValue: "No results for your search."
                          })}
                        </p>
                      )}
                    </div>
                  ) : null}
                  <p className="m-0 mt-1 text-[11px] text-app-ink-muted">
                    {t("new-ui.request-sign.recipient-query-help", {
                      defaultValue: "Autocomplete uses your address book. If it's not there, type name + email and press Enter."
                    })}
                  </p>
                </div>
                <div className="flex w-full flex-wrap items-center gap-2 md:w-auto md:flex-nowrap">
                  <button
                    type="button"
                    onClick={openAddContactModal}
                    className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#1ea34f] bg-[#22c55e] text-white transition hover:bg-[#1ea34f]"
                    aria-label={t("new-ui.request-sign.add-contact", { defaultValue: "Add contact" })}
                    title={t("new-ui.request-sign.add-contact", { defaultValue: "Add contact" })}
                  >
                    <Icon name="plus" className="h-5 w-5" />
                  </button>
                  <label className="inline-flex min-w-[200px] flex-1 items-center gap-2 text-sm text-app-ink cursor-pointer md:min-w-0 md:flex-none">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300"
                      checked={requireBiometricForAll}
                      onChange={(e) => handleRequireBiometricForAllChange(e.target.checked)}
                    />
                    <span>{t("require-biometric-all", { defaultValue: "Solicitar ID a todos" })}</span>
                  </label>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {recipients.length ? (
                  recipients.map((recipient) => (
                    <span
                      key={recipient.id}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${recipient.colorClass}`}
                    >
                      <span className="max-w-[200px] truncate" title={`${recipient.name}`}>
                        {recipient.name}
                      </span>
                      {recipient.requireBiometric && (
                        <Icon name="user" className="h-3.5 w-3.5" title={t("biometric-required", { defaultValue: "Identificación requerida" })} />
                      )}
                      <button
                        type="button"
                        className="rounded-full bg-white/80 p-0.5 text-app-ink hover:text-app-danger"
                        onClick={() => removeRecipient(recipient.id)}
                        aria-label={t("delete", { defaultValue: "Delete" })}
                      >
                        <Icon name="close" className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  ))
                ) : (
                  <p className="m-0 text-xs text-app-ink-muted">
                    {t("new-ui.request-sign.no-recipients", {
                      defaultValue: "No signers added."
                    })}
                  </p>
                )}
              </div>
            </div>

            <button
              type="button"
              className="mt-1 w-fit text-sm font-medium text-indigo-600 underline-offset-2 transition hover:text-indigo-700 hover:underline"
              onClick={() => setIsAdvancedOpen((current) => !current)}
            >
              {isAdvancedOpen
                ? t("hide-advanced-options", { defaultValue: "Hide advanced options" })
                : t("advanced-options", { defaultValue: "Advanced options" })}
            </button>

            {isAdvancedOpen ? (
              <div className="grid grid-cols-1 gap-3 rounded-xl border border-app-line bg-app-surface p-3 lg:grid-cols-2">
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-app-ink-muted">
                      {t("send-sign-reminders", { defaultValue: "Enviar recordatorios de firma" })}
                      <InfoHint text={t("send-sign-reminders-help", { defaultValue: "" })} />
                    </label>
                    <div className="flex items-center gap-3 text-sm">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          className="h-3.5 w-3.5"
                          checked={advancedOptions.automaticReminders === true}
                          onChange={() =>
                            setAdvancedOptions((current) => ({ ...current, automaticReminders: true }))
                          }
                        />
                        <span>{t("yes", { defaultValue: "Yes" })}</span>
                      </label>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          className="h-3.5 w-3.5"
                          checked={advancedOptions.automaticReminders === false}
                          onChange={() =>
                            setAdvancedOptions((current) => ({ ...current, automaticReminders: false }))
                          }
                        />
                        <span>{t("no", { defaultValue: "No" })}</span>
                      </label>
                    </div>
                  </div>

                  {advancedOptions.automaticReminders ? (
                    <Input
                      id="request-sign-remind-once"
                      type="number"
                      label={`${t("remind-once", { defaultValue: "Remind once in every (Days)" })} *`}
                      value={advancedOptions.remindOnceInEvery}
                      onChange={(event) =>
                        setAdvancedOptions((current) => ({
                          ...current,
                          remindOnceInEvery: event.target.value
                        }))
                      }
                      min={1}
                    />
                  ) : null}

                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-app-ink-muted">
                      {t("send-in-order", { defaultValue: "Enviar en orden" })}
                      <InfoHint text={t("send-in-order-help.p1", { defaultValue: "" })} />
                    </label>
                    <div className="flex items-center gap-3 text-sm">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          className="h-3.5 w-3.5"
                          checked={advancedOptions.sendInOrder === true}
                          onChange={() => setAdvancedOptions((current) => ({ ...current, sendInOrder: true }))}
                        />
                        <span>{t("yes", { defaultValue: "Yes" })}</span>
                      </label>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          className="h-3.5 w-3.5"
                          checked={advancedOptions.sendInOrder === false}
                          onChange={() => setAdvancedOptions((current) => ({ ...current, sendInOrder: false }))}
                        />
                        <span>{t("no", { defaultValue: "No" })}</span>
                      </label>
                    </div>
                  </div>

                  <Input
                    id="request-sign-time-to-complete"
                    type="number"
                    label={
                      <>
                        {`${t("time-to-complete", { defaultValue: "Plazo de finalización (días)" })} *`}
                        <InfoHint text={t("time-to-complete-help", { defaultValue: "Días máximos para completar la firma del documento." })} />
                      </>
                    }
                    value={advancedOptions.timeToCompleteDays}
                    onChange={(event) =>
                      setAdvancedOptions((current) => ({
                        ...current,
                        timeToCompleteDays: event.target.value
                      }))
                    }
                    min={1}
                  />
                </div>

                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-app-ink-muted">
                      {t("enable-tour", { defaultValue: "Habilitar tour" })}
                      <InfoHint text={t("istourenabled-help.p1", { defaultValue: "" })} />
                    </label>
                    <div className="flex items-center gap-3 text-sm">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          className="h-3.5 w-3.5"
                          checked={advancedOptions.isTourEnabled === true}
                          onChange={() =>
                            setAdvancedOptions((current) => ({ ...current, isTourEnabled: true }))
                          }
                        />
                        <span>{t("yes", { defaultValue: "Yes" })}</span>
                      </label>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          className="h-3.5 w-3.5"
                          checked={advancedOptions.isTourEnabled === false}
                          onChange={() =>
                            setAdvancedOptions((current) => ({ ...current, isTourEnabled: false }))
                          }
                        />
                        <span>{t("no", { defaultValue: "No" })}</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-app-ink-muted">
                      {t("notify-on-signatures", { defaultValue: "Notificar al firmar" })}
                      <InfoHint text={t("notify-on-signatures-help.p1", { defaultValue: "" })} />
                    </label>
                    <div className="flex items-center gap-3 text-sm">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          className="h-3.5 w-3.5"
                          checked={advancedOptions.notifyOnSignatures === true}
                          onChange={() =>
                            setAdvancedOptions((current) => ({ ...current, notifyOnSignatures: true }))
                          }
                        />
                        <span>{t("yes", { defaultValue: "Yes" })}</span>
                      </label>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          className="h-3.5 w-3.5"
                          checked={advancedOptions.notifyOnSignatures === false}
                          onChange={() =>
                            setAdvancedOptions((current) => ({ ...current, notifyOnSignatures: false }))
                          }
                        />
                        <span>{t("no", { defaultValue: "No" })}</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-app-ink-muted">
                      {t("isenable-otp", { defaultValue: "Habilitar verificación OTP" })}
                      <InfoHint text={t("isenable-otp-help.p1", { defaultValue: "" })} />
                    </label>
                    <div className="flex items-center gap-3 text-sm">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          className="h-3.5 w-3.5"
                          checked={advancedOptions.isEnableOTP === true}
                          onChange={() => setAdvancedOptions((current) => ({ ...current, isEnableOTP: true }))}
                        />
                        <span>{t("yes", { defaultValue: "Yes" })}</span>
                      </label>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          className="h-3.5 w-3.5"
                          checked={advancedOptions.isEnableOTP === false}
                          onChange={() => setAdvancedOptions((current) => ({ ...current, isEnableOTP: false }))}
                        />
                        <span>{t("no", { defaultValue: "No" })}</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="request-sign-redirect-url" className="mb-1.5 block text-xs font-semibold text-app-ink-muted">
                      {t("redirect-url", { defaultValue: "URL de redireccionamiento" })}
                      <InfoHint text={t("redirect-url-help", {
                        defaultValue: "Después de firmar, redirigir al firmante a esta URL. Debe comenzar con http:// o https://"
                      })} />
                    </label>
                    <Input
                      id="request-sign-redirect-url"
                      value={advancedOptions.redirectUrl}
                      onChange={(event) =>
                        setAdvancedOptions((current) => ({ ...current, redirectUrl: event.target.value }))
                      }
                      placeholder="https://ejemplo.com"
                    />
                  </div>
                </div>
              </div>
            ) : null}

            <div className="mt-1 flex flex-wrap gap-2">
              <Button type="submit" disabled={isUploading || isSubmitting || !uploadedFileUrl}>
                {isSubmitting
                  ? t("loading", { defaultValue: "Loading..." })
                  : t("new-ui.request-sign.open-editor", { defaultValue: "Open editor" })}
              </Button>
              <Button type="button" variant="ghost" onClick={clearForm} disabled={isSubmitting}>
                {t("reset", { defaultValue: "Reset" })}
              </Button>
              <Link to="/dashboard/35KBoSgoAK">
                <Button type="button" variant="ghost">
                  {t("new-ui.sections.back-dashboard", { defaultValue: "Back to dashboard" })}
                </Button>
              </Link>
            </div>
          </form>
        </Card>
      </div>

      <Modal
        isOpen={isAddContactModalOpen}
        title={t("new-ui.request-sign.add-contact-modal-title", { defaultValue: "Add contact" })}
        onClose={closeAddContactModal}
        closeIconOnly
        closeLabel={t("close", { defaultValue: "Close" })}
      >
        <div className="grid grid-cols-1 gap-3">
          <Input
            id="request-sign-new-contact-name"
            label={t("name", { defaultValue: "Name" })}
            error={contactFormFieldErrors.Name}
            value={contactForm.Name}
            onChange={(event) => {
              setContactForm((current) => ({ ...current, Name: event.target.value }));
              setContactFormError("");
              setContactFormFieldErrors((current) => ({ ...current, Name: "" }));
            }}
            maxLength={120}
          />
          <Input
            id="request-sign-new-contact-email"
            type="email"
            label={t("email", { defaultValue: "Email" })}
            error={contactFormFieldErrors.Email}
            value={contactForm.Email}
            onChange={(event) => {
              setContactForm((current) => ({
                ...current,
                Email: String(event.target.value || "").toLowerCase().replace(/\s/g, "")
              }));
              setContactFormError("");
              setContactFormFieldErrors((current) => ({ ...current, Email: "" }));
            }}
            maxLength={160}
          />
          <Input
            id="request-sign-new-contact-phone"
            label={t("phone", { defaultValue: "Phone" })}
            value={contactForm.Phone}
            onChange={(event) => {
              setContactForm((current) => ({ ...current, Phone: event.target.value }));
              setContactFormError("");
            }}
            placeholder={t("phone-optional", { defaultValue: "optional" })}
            maxLength={40}
          />
          {contactFormError ? <p className="m-0 text-sm text-app-danger">{contactFormError}</p> : null}
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="ghost" onClick={closeAddContactModal} disabled={isSavingContact}>
              {t("cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button type="button" onClick={saveContactAndAddRecipient} disabled={isSavingContact}>
              {isSavingContact ? t("loading", { defaultValue: "Loading..." }) : t("submit", { defaultValue: "Submit" })}
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={confirmDelete.show}
        title={t("confirm-remove-signer-title", { defaultValue: "Eliminar firmante" })}
        message={t("confirm-remove-signer", {
          defaultValue: `¿Está seguro de que desea eliminar a {{name}}? Todos los widgets asignados a este firmante también se eliminarán.`,
          name: confirmDelete.recipientName
        })}
        confirmText={t("delete", { defaultValue: "Eliminar" })}
        cancelText={t("cancel", { defaultValue: "Cancelar" })}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        type="danger"
      />

      <Modal
        isOpen={showDocumentBackModal}
        title={t("require-document-back-modal-title", { defaultValue: "Solicitar reverso del documento" })}
        onClose={handleDocumentBackModalCancel}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            {t("require-document-back-modal-message", {
              defaultValue: "¿Desea solicitar también el reverso del documento de identidad a todos los firmantes?"
            })}
          </p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => handleDocumentBackModalConfirm(false)}>
              {t("no", { defaultValue: "No" })}
            </Button>
            <Button type="button" variant="primary" onClick={() => handleDocumentBackModalConfirm(true)}>
              {t("yes", { defaultValue: "Sí" })}
            </Button>
          </div>
        </div>
      </Modal>
    </AppShell>
  );
}

export default RequestSignPage;
